import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { useAuthBootstrap } from './context/AuthContext';
import { useSubscriptionBootstrap } from './context/SubscriptionContext';
import useNativeSetup from './hooks/useNativeSetup';
import useDexieSync from './hooks/useDexieSync';
import useThemeSync from './hooks/useThemeSync';
import UpgradeModal from './components/subscription/UpgradeModal';
import DeepLinkHandler from './components/DeepLinkHandler';
import { Toaster } from 'react-hot-toast';
import Layout from './components/layout/Layout';
import PublicRoute from './components/auth/PublicRoute';
import ExplorePage from './pages/ExplorePage';
import RecipesPage from './pages/RecipesPage';
import RecipeDetailPage from './pages/RecipeDetailPage';
import RecipeEditorPage from './pages/RecipeEditorPage';
import MyCookbookPage from './pages/MyCookbookPage';
import MealPlanPage from './pages/MealPlanPage';
import GroceryPage from './pages/GroceryPage';
import CookingModePage from './pages/CookingModePage';
import ImportPage from './pages/ImportPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import VerifyPage from './pages/VerifyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import './App.css';

function AppRoot() {
  useNativeSetup();
  useAuthBootstrap();
  useSubscriptionBootstrap();
  useDexieSync();
  useThemeSync();
  return (
    <>
      <Router>
        <UpgradeModal />
        <DeepLinkHandler />
        <Routes>
          {/* Auth routes — outside layout. PublicRoute redirects signed-in users to /. */}
          <Route element={<PublicRoute />}>
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/signup" element={<SignupPage />} />
            <Route path="/auth/verify" element={<VerifyPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          </Route>
          {/* Callback must NOT redirect — Cognito hands the code here before auth state settles. */}
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />

          {/* Legal pages — outside layout */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Cooking mode is full-screen, outside layout */}
          <Route path="/recipes/:id/cook" element={<CookingModePage />} />

          {/* All other routes use the standard layout */}
          <Route element={<Layout />}>
            <Route path="/" element={<ExplorePage />} />
            <Route path="/browse" element={<RecipesPage />} />
            <Route path="/recipes/:id" element={<RecipeDetailPage />} />
            <Route path="/cookbook" element={<MyCookbookPage />} />
            <Route path="/import" element={<ImportPage />} />
            <Route path="/submit" element={<RecipeEditorPage />} />
            <Route path="/submit/:id/edit" element={<RecipeEditorPage />} />
            <Route path="/meal-plan" element={<MealPlanPage />} />
            <Route path="/grocery" element={<GroceryPage />} />
            <Route path="/profile" element={<SettingsPage />} />
          </Route>
        </Routes>
      </Router>
      <Toaster
        position="top-center"
        containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
        toastOptions={{
          duration: 3500,
          style: {
            borderRadius: '14px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
            maxWidth: '92vw',
            background: 'var(--toast-bg)',
            color: 'var(--toast-fg)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
            border: '1px solid var(--toast-border)',
          },
          success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
        }}
      />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppRoot />
    </QueryClientProvider>
  );
}

export default App;
